/**
 * Patient360 — Call Logs Tab (Operational Communication Workspace)
 *
 * Layout:
 * - KPI strip (Total / Completed / Follow-up / Escalated)
 * - Filter chips + search
 * - 2-column: Left (timeline cards) + Right (insights/follow-ups/stats)
 * - Right drawer for Add Call Log (no page redirect)
 * - Timeline grouping by date
 */

import { memo, useMemo, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  AlertCircle,
  Calendar,
  MoreVertical,
  Phone,
  PhoneCall,
  PhoneOff,
  Plus,
  Search,
  X,
} from 'lucide-react';
import { useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import { colorSystem } from '@/design-system/colorSystem';
import { AppInlineLoader } from '@/modules/shared/components/ui/AppLoading';
import { Button } from '@/modules/shared/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/modules/shared/components/ui/dropdown-menu';
import { StatusBadge } from '@/modules/shared/components/ui/StatusBadge';
import { useToast } from '@/modules/shared/components/ui/toast';
import { formatDisplayDate } from '../../../utils/formatUtils';
import type { Patient360CallDto } from '../../../types/patient360';
import { useProgramAccess } from '../../../hooks/useProgramAccess';
import { PROGRAM_ACTION_KEYS } from '../../../services/programAccessService';
import { callLogService } from '../../../services/callLogService';
import { patient360QueryKeys } from '../../../hooks/usePatient360';
import { TabGuidancePanel } from '../TabGuidancePanel';
import type { GuidanceItem } from '../../../types/guidance';

// ─── Types ─────────────────────────────────────────────────────

interface CallLogsTabProps {
  calls: Patient360CallDto[];
  patientName?: string;
  patientCode?: string;
  onAddCallLog?: () => void;
}

type CallFilter = 'all' | 'completed' | 'pending' | 'escalated' | 'followup' | 'missed';

// ─── Helpers ───────────────────────────────────────────────────

function getCallStatusInfo(status: string): { variant: 'success' | 'warning' | 'muted' | 'error' | 'info'; label: string; color: string } {
  const normalized = status?.toUpperCase() || '';
  switch (normalized) {
    case 'COMPLETED':
    case 'ANSWERED':
      return { variant: 'success', label: 'Completed', color: 'text-emerald-600' };
    case 'MISSED':
    case 'NO_ANSWER':
    case 'NOT_ANSWERED':
      return { variant: 'muted', label: 'Missed', color: 'text-muted-foreground' };
    case 'SCHEDULED':
    case 'PENDING':
      return { variant: 'warning', label: 'Pending', color: 'text-amber-600' };
    case 'FOLLOW_UP':
    case 'FOLLOWUP':
      return { variant: 'info', label: 'Follow-up', color: 'text-blue-600' };
    case 'ESCALATED':
      return { variant: 'error', label: 'Escalated', color: 'text-destructive' };
    case 'IN_PROGRESS':
      return { variant: 'info', label: 'In Progress', color: 'text-blue-600' };
    case 'CANCELLED':
      return { variant: 'muted', label: 'Cancelled', color: 'text-muted-foreground' };
    default:
      return { variant: 'muted', label: status || 'Unknown', color: 'text-muted-foreground' };
  }
}

function getCallTypeIcon(callType: string) {
  const normalized = callType?.toUpperCase() || '';
  switch (normalized) {
    case 'INBOUND':
    case 'INCOMING':
      return { icon: PhoneCall, cls: 'text-emerald-600 bg-emerald-50 dark:bg-emerald-950/30' };
    case 'OUTBOUND':
    case 'OUTGOING':
      return { icon: Phone, cls: 'text-primary bg-primary/10' };
    case 'MISSED':
      return { icon: PhoneOff, cls: 'text-destructive bg-destructive/10' };
    default:
      return { icon: Phone, cls: 'text-muted-foreground bg-muted/40' };
  }
}

function formatTime(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

function groupByDate(calls: Patient360CallDto[]): Map<string, Patient360CallDto[]> {
  const groups = new Map<string, Patient360CallDto[]>();
  for (const call of calls) {
    const date = formatDisplayDate(call.callDateTime);
    if (!groups.has(date)) groups.set(date, []);
    groups.get(date)!.push(call);
  }
  return groups;
}

function matchesFilter(call: Patient360CallDto, filter: CallFilter): boolean {
  const upper = call.callStatus?.toUpperCase() ?? '';
  switch (filter) {
    case 'all': return true;
    case 'completed': return ['COMPLETED', 'ANSWERED'].includes(upper);
    case 'pending': return ['SCHEDULED', 'PENDING', 'IN_PROGRESS'].includes(upper);
    case 'escalated': return upper === 'ESCALATED';
    case 'followup': return call.followUpRequired;
    case 'missed': return ['MISSED', 'NO_ANSWER', 'NOT_ANSWERED'].includes(upper);
  }
}

function matchesSearch(call: Patient360CallDto, query: string): boolean {
  if (!query) return true;
  const q = query.toLowerCase();
  return (
    (call.notes?.toLowerCase().includes(q) ?? false) ||
    (call.callType?.toLowerCase().includes(q) ?? false) ||
    (call.entityName?.toLowerCase().includes(q) ?? false) ||
    (call.createdBy?.toLowerCase().includes(q) ?? false)
  );
}

// ─── Sub-Components ────────────────────────────────────────────

/** Timeline Call Card — premium card */
function TimelineCallCard({ call }: { call: Patient360CallDto }) {
  const statusInfo = getCallStatusInfo(call.callStatus);
  const typeInfo = getCallTypeIcon(call.callType);
  const Icon = typeInfo.icon;

  const displayBy = call.createdByName || call.createdByRole || (call.createdBy?.length === 36 ? 'System' : call.createdBy) || 'System';

  return (
    <div
      className="group relative rounded-xl border border-[#DBEAFE] dark:border-border/50 px-4 py-3.5 cursor-pointer transition-all duration-200 hover:border-[#93C5FD] hover:-translate-y-0.5 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_8px_22px_rgba(37,99,235,0.06)] bg-gradient-to-b from-white to-[#F8FBFF] dark:from-[#111827] dark:to-[#151C2C]"
    >
      <div className="flex gap-3">
        {/* Icon */}
        <div className={cn('flex h-8 w-8 shrink-0 items-center justify-center rounded-lg', typeInfo.cls)}>
          <Icon className="h-3.5 w-3.5" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Row 1: Type + Status */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <span className="text-[12px] font-semibold text-foreground capitalize">
                {call.callType?.toLowerCase() || 'Call'}
              </span>
              <StatusBadge status={statusInfo.label} variant={statusInfo.variant} />
            </div>
            <span className="text-[11px] text-muted-foreground shrink-0">
              {formatTime(call.callDateTime)}
            </span>
          </div>

          {/* Row 2: Notes */}
          {call.notes && (
            <p className="text-[11px] text-muted-foreground mt-1.5 line-clamp-2 leading-relaxed">{call.notes}</p>
          )}

          {/* Row 3: Metadata */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-2 text-[11px] text-muted-foreground">
            {call.entityName && (
              <span>Contact: <span className="font-medium text-foreground">{call.entityName}</span></span>
            )}
            <span>By: <span className="font-medium text-foreground">{displayBy}</span></span>
            {call.durationMinutes != null && (
              <span>Duration: <span className="font-medium text-foreground">{call.durationMinutes} min</span></span>
            )}
            {call.caseId && (
              <span className="text-muted-foreground">Case linked</span>
            )}
          </div>

          {/* Follow-up indicator */}
          {call.followUpRequired && (
            <div className="flex items-center gap-1.5 mt-2 text-[11px] font-medium" style={{ color: colorSystem.warning.dark }}>
              <AlertCircle className="h-3 w-3" />
              <span>
                Follow-up{call.nextFollowUpDate ? ` · ${formatDisplayDate(call.nextFollowUpDate)}` : ' required'}
              </span>
            </div>
          )}
        </div>

        {/* Kebab menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
              <MoreVertical className="h-3.5 w-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-36">
            <DropdownMenuItem className="text-[11px]">View Details</DropdownMenuItem>
            <DropdownMenuItem className="text-[11px]">Edit</DropdownMenuItem>
            <DropdownMenuItem className="text-[11px]">Mark Follow-up</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

/**
 * Compact horizontal summary strip — replaces the old permanent right-side
 * insights sidebar. Reuses the same filterCounts already computed by the
 * parent (no new calculation) so the call history below can use the full
 * page width instead of sharing it with a 260px column.
 */
function CallSummaryStrip({ counts }: {
  counts: { completed: number; pending: number; missed: number; escalated: number; all: number };
}) {
  const completionRate = counts.all > 0 ? Math.round((counts.completed / counts.all) * 100) : 0;
  const rateColor = completionRate >= 80 ? colorSystem.success.dark : completionRate >= 50 ? colorSystem.warning.dark : colorSystem.danger.dark;

  const items: { label: string; value: number; color: string }[] = [
    { label: 'Completed', value: counts.completed, color: colorSystem.success.dark },
    { label: 'Pending', value: counts.pending, color: colorSystem.warning.dark },
    { label: 'Missed', value: counts.missed, color: colorSystem.muted.dark },
    { label: 'Escalated', value: counts.escalated, color: colorSystem.danger.dark },
  ];

  return (
    <div className="rounded-lg border border-border/50 bg-muted/10 px-3 py-2 flex flex-wrap items-center gap-x-5 gap-y-1.5">
      {items.map((item) => (
        <div key={item.label} className="flex items-center gap-1.5">
          <span className="text-[13px] font-bold" style={{ color: item.color }}>{item.value}</span>
          <span className="text-[11px] text-muted-foreground">{item.label}</span>
        </div>
      ))}
      <div className="flex items-center gap-1.5 sm:ml-auto">
        <span className="text-[11px] text-muted-foreground">Completion Rate</span>
        <span className="text-[13px] font-bold" style={{ color: rateColor }}>{completionRate}%</span>
      </div>
    </div>
  );
}

/** Empty state — premium */
function CallLogsEmptyState({ canCreate, onAdd }: { canCreate: boolean; onAdd?: () => void }) {
  return (
    <div className="rounded-xl border border-border/50 bg-[#F8FAFC] dark:bg-muted/10 p-6 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
      <div className="flex flex-col items-center text-center max-w-sm mx-auto">
        <div className="rounded-xl bg-muted/40 p-3 mb-3">
          <Phone className="h-5 w-5 text-muted-foreground" />
        </div>
        <h4 className="text-[14px] font-bold text-foreground">No Call Logs</h4>
        <p className="text-[11px] text-muted-foreground mt-1 leading-relaxed">
          Call history will appear here once interactions are logged.
        </p>
        <div className="mt-3 grid grid-cols-2 gap-2 text-[11px] text-muted-foreground w-full">
          <div className="flex items-center gap-1.5 rounded-md bg-white dark:bg-background border border-border/40 px-2 py-1.5">
            <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: colorSystem.primary.dark }} />
            Patient inquiries
          </div>
          <div className="flex items-center gap-1.5 rounded-md bg-white dark:bg-background border border-border/40 px-2 py-1.5">
            <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: colorSystem.success.dark }} />
            Follow-up calls
          </div>
          <div className="flex items-center gap-1.5 rounded-md bg-white dark:bg-background border border-border/40 px-2 py-1.5">
            <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: colorSystem.warning.dark }} />
            Treatment queries
          </div>
          <div className="flex items-center gap-1.5 rounded-md bg-white dark:bg-background border border-border/40 px-2 py-1.5">
            <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: colorSystem.info.dark }} />
            Escalations
          </div>
        </div>
        {canCreate && onAdd && (
          <Button size="sm" className="h-7 gap-1 text-[11px] mt-4" onClick={onAdd}>
            <Plus className="h-3 w-3" /> Log First Call
          </Button>
        )}
      </div>
    </div>
  );
}

// ─── Add Call Log Drawer ───────────────────────────────────────

// ─── Validation Types ──────────────────────────────────────────

interface CallLogFormErrors {
  callType?: string;
  callStatus?: string;
  callDateTime?: string;
  notes?: string;
  duration?: string;
  followUpDate?: string;
}

function validateCallLogForm(values: {
  callType: string;
  callStatus: string;
  callDateTime: string;
  notes: string;
  duration: string;
  followUp: boolean;
  followUpDate: string;
}): CallLogFormErrors {
  const errors: CallLogFormErrors = {};

  if (!values.callType) errors.callType = 'Call type is required';
  if (!values.callStatus) errors.callStatus = 'Please select Call Outcome.';
  if (!values.callDateTime) errors.callDateTime = 'Call date and time is required';
  if (!values.notes.trim()) errors.notes = 'Notes are required';

  if (values.callStatus === 'COMPLETED') {
    const dur = Number(values.duration);
    if (!values.duration || Number.isNaN(dur) || dur <= 0) {
      errors.duration = 'Duration is required for completed calls';
    }
  }

  if (values.followUp && !values.followUpDate) {
    errors.followUpDate = 'Follow-up date is required';
  }

  return errors;
}

// ─── Add Call Log Drawer ───────────────────────────────────────

function AddCallLogDrawer({ open, onClose, programId, patientId, patientName, patientCode }: {
  open: boolean;
  onClose: () => void;
  programId: string;
  patientId: string;
  patientName?: string;
  patientCode?: string;
}) {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const [callType, setCallType] = useState('OUTBOUND');
  const [callStatus, setCallStatus] = useState('');
  const [notes, setNotes] = useState('');
  const [followUp, setFollowUp] = useState(false);
  const [followUpDate, setFollowUpDate] = useState('');
  const [duration, setDuration] = useState('');
  const [callDateTime, setCallDateTime] = useState(() => {
    // Initialize with local time for datetime-local input
    const now = new Date();
    const offset = now.getTimezoneOffset();
    return new Date(now.getTime() - offset * 60000).toISOString().slice(0, 16);
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<CallLogFormErrors>({});

  const resetForm = useCallback(() => {
    setCallType('OUTBOUND');
    setCallStatus('');
    setNotes('');
    setFollowUp(false);
    setFollowUpDate('');
    setDuration('');
    setCallDateTime(() => {
      const now = new Date();
      const offset = now.getTimezoneOffset();
      return new Date(now.getTime() - offset * 60000).toISOString().slice(0, 16);
    });
    setErrors({});
  }, []);

  const handleSave = useCallback(async () => {
    // Validate
    const formErrors = validateCallLogForm({
      callType, callStatus, callDateTime, notes, duration, followUp, followUpDate,
    });

    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      showToast('Please fix the highlighted fields', 'error');
      return;
    }

    setErrors({});

    try {
      setSaving(true);

      await callLogService.create(programId, {
        programId,
        entityType: 'PATIENT',
        entityId: patientId,
        entityName: patientName || null,
        callType,
        callStatus,
        callDateTime: new Date(callDateTime).toISOString(),
        notes: notes.trim(),
        durationMinutes: duration ? Number(duration) : null,
        followUpRequired: followUp,
        nextFollowUpDate: followUp && followUpDate ? followUpDate : null,
      });

      showToast('Call log saved successfully', 'success');
      resetForm();
      onClose();

      // Refresh patient360 data so the new call log appears in the list
      await queryClient.invalidateQueries({
        queryKey: patient360QueryKeys.detail(patientId),
      });
    } catch (error: unknown) {
      const msg =
        error instanceof Error ? error.message
          : (error as { response?: { data?: { message?: string } } })?.response?.data?.message
            || 'Failed to save call log. Please try again.';
      showToast(msg, 'error');
    } finally {
      setSaving(false);
    }
  }, [callType, callStatus, callDateTime, notes, duration, followUp, followUpDate, programId, patientId, patientName, onClose, resetForm, showToast, queryClient]);

  if (!open) return null;

  return createPortal(
    <>
      {/* Full-screen saving overlay */}
      {saving && (
        <div className="app-right-drawer-saving-overlay">
          <AppInlineLoader className="text-white" label="Loading content" />
          <p className="text-white text-[12px] font-medium mt-4">Saving call log...</p>
        </div>
      )}

      {/* Backdrop — starts below app header */}
      <div className="app-right-drawer-backdrop" onClick={onClose} />

      {/* Drawer — starts below app header */}
      <div className="app-right-drawer animate-in slide-in-from-right duration-300">

        {/* Header */}
        <div className="app-right-drawer-header">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                <Phone className="h-4 w-4 text-primary" />
              </div>
              <div>
                <h2 className="text-[14px] font-semibold text-foreground">Add Call Log</h2>
                <p className="text-[11px] text-muted-foreground mt-0.5">Record patient interaction details</p>
              </div>
            </div>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 rounded-md" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Scrollable Body */}
        <div className="app-right-drawer-body space-y-4">

          {/* Patient context bar */}
          <div className="rounded-lg border border-primary/20 bg-primary/5 px-4 py-3 flex items-center gap-3">
            <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
              <PhoneCall className="h-3.5 w-3.5 text-primary" />
            </div>
            <div className="text-[12px] leading-tight min-w-0">
              <p className="font-semibold text-foreground truncate">{patientName || 'Patient'}</p>
              {patientCode && <p className="text-[11px] text-muted-foreground mt-0.5">{patientCode}</p>}
            </div>
          </div>

          {/* Call Details — form card matching workspace AppCard */}
          <div className="rounded-lg border border-border bg-card">
            <div className="p-4 pb-3 border-b border-border">
              <p className="text-[12px] font-semibold text-foreground">Call Details</p>
            </div>
            <div className="p-4 pt-3 space-y-3">
              <div className="grid grid-cols-12 gap-3">
                {/* Call Type */}
                <div className="col-span-6 space-y-1.5">
                  <label className="text-[11px] font-medium text-foreground block">Call Type <span className="text-destructive">*</span></label>
                  <select
                    value={callType}
                    onChange={(e) => { setCallType(e.target.value); setErrors(prev => ({ ...prev, callType: undefined })); }}
                    className={cn(
                      "w-full h-9 rounded-md border bg-background px-3 text-[12px] focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all outline-none",
                      errors.callType ? "border-destructive/45 shadow-[0_0_0_3px_rgba(220,38,38,0.08)]" : "border-border",
                    )}
                  >
                    <option value="OUTBOUND">Outbound</option>
                    <option value="INBOUND">Inbound</option>
                    <option value="MISSED">Missed</option>
                  </select>
                  {errors.callType && <p className="text-[11px] font-medium text-destructive mt-1">{errors.callType}</p>}
                </div>

                {/* Call Outcome */}
                <div className="col-span-6 space-y-1.5">
                  <label className="text-[11px] font-medium text-foreground block">Call Outcome <span className="text-destructive">*</span></label>
                  <select
                    value={callStatus}
                    onChange={(e) => { setCallStatus(e.target.value); setErrors(prev => ({ ...prev, callStatus: undefined })); }}
                    className={cn(
                      "w-full h-9 rounded-md border bg-background px-3 text-[12px] focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all outline-none",
                      errors.callStatus ? "border-destructive/45 shadow-[0_0_0_3px_rgba(220,38,38,0.08)]" : "border-border",
                    )}
                  >
                    <option value="" disabled>Select Call Outcome</option>
                    <option value="COMPLETED">Completed</option>
                    <option value="PENDING">Pending</option>
                    <option value="SCHEDULED">Scheduled</option>
                    <option value="ESCALATED">Escalated</option>
                    <option value="FOLLOW_UP">Follow-up Required</option>
                  </select>
                  {errors.callStatus && <p className="text-[11px] font-medium text-destructive mt-1">{errors.callStatus}</p>}
                </div>

                {/* Duration */}
                <div className="col-span-6 space-y-1.5">
                  <label className="text-[11px] font-medium text-foreground block">
                    Duration (minutes){callStatus === 'COMPLETED' && <span className="text-destructive"> *</span>}
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={duration}
                    onChange={(e) => { setDuration(e.target.value); setErrors(prev => ({ ...prev, duration: undefined })); }}
                    placeholder="e.g. 15"
                    className={cn(
                      "w-full h-9 rounded-md border bg-background px-3 text-[12px] placeholder:text-muted-foreground focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all outline-none",
                      errors.duration ? "border-destructive/45 shadow-[0_0_0_3px_rgba(220,38,38,0.08)]" : "border-border",
                    )}
                  />
                  {errors.duration && <p className="text-[11px] font-medium text-destructive mt-1">{errors.duration}</p>}
                </div>

                {/* Call Date/Time */}
                <div className="col-span-6 space-y-1.5">
                  <label className="text-[11px] font-medium text-foreground block">Call Date & Time <span className="text-destructive">*</span></label>
                  <input
                    type="datetime-local"
                    value={callDateTime}
                    onChange={(e) => { setCallDateTime(e.target.value); setErrors(prev => ({ ...prev, callDateTime: undefined })); }}
                    className={cn(
                      "w-full h-9 rounded-md border bg-background px-3 text-[12px] focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all outline-none",
                      errors.callDateTime ? "border-destructive/45 shadow-[0_0_0_3px_rgba(220,38,38,0.08)]" : "border-border",
                    )}
                  />
                  <p className="text-[11px] text-muted-foreground">Local Time ({Intl.DateTimeFormat().resolvedOptions().timeZone})</p>
                  {errors.callDateTime && <p className="text-[11px] font-medium text-destructive mt-1">{errors.callDateTime}</p>}
                </div>

                {/* Notes — full width */}
                <div className="col-span-12 space-y-1.5">
                  <label className="text-[11px] font-medium text-foreground block">Notes <span className="text-destructive">*</span></label>
                  <textarea
                    value={notes}
                    onChange={(e) => { setNotes(e.target.value); setErrors(prev => ({ ...prev, notes: undefined })); }}
                    placeholder="Call summary, actions taken, patient feedback..."
                    className={cn(
                      "w-full min-h-[120px] resize-none rounded-md border bg-background px-3 py-2 text-[12px] placeholder:text-muted-foreground focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all outline-none",
                      errors.notes ? "border-destructive/45 shadow-[0_0_0_3px_rgba(220,38,38,0.08)]" : "border-border",
                    )}
                  />
                  {errors.notes && <p className="text-[11px] font-medium text-destructive mt-1">{errors.notes}</p>}
                </div>
              </div>
            </div>
          </div>

          {/* Follow-up — form card matching workspace AppCard */}
          <div className="rounded-lg border border-border bg-card">
            <div className="p-4 pb-3 border-b border-border">
              <p className="text-[12px] font-semibold text-foreground">Follow-up</p>
            </div>
            <div className="p-4 pt-3 space-y-3">
              {/* Toggle row */}
              <div className="flex items-center justify-between rounded-xl border border-border bg-muted/30 px-4 py-3.5">
                <div className="pr-3">
                  <p className="text-[13px] font-semibold text-foreground">Follow-up Required</p>
                  <p className="text-[12px] text-muted-foreground mt-0.5">Schedule a future patient interaction</p>
                </div>
                <label className="app-switch">
                  <input
                    type="checkbox"
                    checked={followUp}
                    onChange={(e) => { setFollowUp(e.target.checked); if (!e.target.checked) setErrors(prev => ({ ...prev, followUpDate: undefined })); }}
                    aria-label="Follow-up required"
                  />
                  <span className="app-switch-track">
                    <span className="app-switch-thumb" />
                  </span>
                </label>
              </div>

              {/* Follow-up date */}
              {followUp && (
                <div className="animate-in fade-in slide-in-from-top-1 duration-200 space-y-1.5">
                  <label className="text-[11px] font-medium text-foreground block">Follow-up Date <span className="text-destructive">*</span></label>
                  <input
                    type="date"
                    min={new Date().toISOString().split('T')[0]}
                    value={followUpDate}
                    onChange={(e) => { setFollowUpDate(e.target.value); setErrors(prev => ({ ...prev, followUpDate: undefined })); }}
                    className={cn(
                      "w-full h-9 rounded-md border bg-background px-3 text-[12px] focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all outline-none",
                      errors.followUpDate ? "border-destructive/45 shadow-[0_0_0_3px_rgba(220,38,38,0.08)]" : "border-border",
                    )}
                  />
                  {errors.followUpDate && <p className="text-[11px] font-medium text-destructive mt-1">{errors.followUpDate}</p>}
                  {!errors.followUpDate && <p className="text-[11px] text-muted-foreground">Past dates are not allowed</p>}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="app-right-drawer-footer">
          <Button variant="outline" size="sm" className="h-9 px-4 text-[12px]" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button size="sm" className="h-9 px-4 text-[12px]" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save Call Log'}
          </Button>
        </div>
      </div>
    </>,
    document.body,
  );
}

// ─── Main Component ────────────────────────────────────────────

export const CallLogsTab = memo(function CallLogsTab({ calls, patientName, patientCode, onAddCallLog }: CallLogsTabProps) {
  const { programId, patientId } = useParams<{ programId: string; patientId: string }>();
  const { hasAction } = useProgramAccess({ programId });
  const canCreateCallLog = hasAction(PROGRAM_ACTION_KEYS.CALL_LOG_CREATE);

  const [filter, setFilter] = useState<CallFilter>('all');
  const [search, setSearch] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Sort calls by date (most recent first)
  const sortedCalls = useMemo(() =>
    [...calls].sort((a, b) => new Date(b.callDateTime).getTime() - new Date(a.callDateTime).getTime()),
    [calls],
  );

  // Filter + Search
  const filteredCalls = useMemo(() =>
    sortedCalls.filter(c => matchesFilter(c, filter) && matchesSearch(c, search)),
    [sortedCalls, filter, search],
  );

  // Group by date for timeline
  const grouped = useMemo(() => groupByDate(filteredCalls), [filteredCalls]);

  // Filter counts
  const filterCounts = useMemo(() => ({
    all: calls.length,
    completed: calls.filter(c => ['COMPLETED', 'ANSWERED'].includes(c.callStatus?.toUpperCase() ?? '')).length,
    pending: calls.filter(c => ['SCHEDULED', 'PENDING', 'IN_PROGRESS'].includes(c.callStatus?.toUpperCase() ?? '')).length,
    escalated: calls.filter(c => c.callStatus?.toUpperCase() === 'ESCALATED').length,
    followup: calls.filter(c => c.followUpRequired).length,
    missed: calls.filter(c => ['MISSED', 'NO_ANSWER', 'NOT_ANSWERED'].includes(c.callStatus?.toUpperCase() ?? '')).length,
  }), [calls]);

  const handleAddClick = useCallback(() => {
    if (onAddCallLog) {
      onAddCallLog();
    } else {
      setDrawerOpen(true);
    }
  }, [onAddCallLog]);

  const guidanceItems: GuidanceItem[] = useMemo(() => {
    const items: GuidanceItem[] = [];
    if (filterCounts.escalated > 0) {
      items.push({
        id: 'calls-escalated',
        severity: 'blocking',
        issue: `${filterCounts.escalated} escalated call${filterCounts.escalated === 1 ? '' : 's'}`,
        detail: 'Escalated calls need review.',
        primaryAction: { label: 'View Escalated', onClick: () => setFilter('escalated') },
      });
    }
    if (filterCounts.followup > 0) {
      items.push({
        id: 'calls-followup',
        severity: 'warning',
        issue: `${filterCounts.followup} call${filterCounts.followup === 1 ? '' : 's'} require follow-up`,
        primaryAction: { label: 'View Follow-ups', onClick: () => setFilter('followup') },
      });
    }
    if (calls.length === 0) {
      items.push({
        id: 'no-calls',
        severity: 'info',
        issue: 'No call logs recorded yet',
        primaryAction: canCreateCallLog
          ? { label: 'Add Call', onClick: handleAddClick }
          : { label: 'Add Call', disabledReason: 'You do not have permission to add call logs.' },
      });
    }
    return items;
  }, [filterCounts.escalated, filterCounts.followup, calls.length, canCreateCallLog, handleAddClick]);

  return (
    <div className="space-y-3">
      <TabGuidancePanel items={guidanceItems} emptyMessage="No call-log follow-up action required." />
      {/* Filter bar + Search + Add button */}
      <div className="flex flex-wrap items-center gap-1.5">
        {([
          { key: 'all' as CallFilter, label: 'All' },
          { key: 'completed' as CallFilter, label: 'Completed' },
          { key: 'pending' as CallFilter, label: 'Pending' },
          { key: 'escalated' as CallFilter, label: 'Escalated' },
          { key: 'followup' as CallFilter, label: 'Follow-up' },
          { key: 'missed' as CallFilter, label: 'Missed' },
        ]).map(f => (
          <button
            key={f.key}
            type="button"
            onClick={() => setFilter(f.key)}
            className={cn(
              'inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-[11px] font-medium transition-colors',
              filter === f.key
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted/60 text-muted-foreground hover:bg-accent',
            )}
          >
            {f.label}
            {filterCounts[f.key] > 0 && <span className="opacity-70">({filterCounts[f.key]})</span>}
          </button>
        ))}

        {/* Search + Add */}
        <div className="ml-auto flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search notes..."
              className="h-7 w-[140px] rounded-md border border-border bg-background pl-7 pr-2 text-[11px] placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
          {canCreateCallLog && (
            <Button size="sm" className="h-7 gap-1 text-[11px]" onClick={handleAddClick}>
              <Plus className="h-3 w-3" /> Add Call
            </Button>
          )}
        </div>
      </div>

      {/* Compact summary strip (was a permanent 260px right sidebar) + full-width call history */}
      {calls.length > 0 ? (
        <div className="space-y-3">
          <CallSummaryStrip counts={filterCounts} />

          <div className="space-y-4 min-w-0">
            {filteredCalls.length === 0 ? (
              <div className="rounded-xl border border-dashed border-border/50 bg-[#F8FAFC] dark:bg-muted/10 p-4 text-center">
                <p className="text-[11px] text-muted-foreground">No calls match this filter.</p>
              </div>
            ) : (
              Array.from(grouped.entries()).map(([dateLabel, dateCalls]) => (
                <div key={dateLabel}>
                  {/* Date header */}
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar className="h-3 w-3 text-muted-foreground" />
                    <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">{dateLabel}</span>
                    <div className="flex-1 h-px bg-border/60" />
                  </div>

                  {/* Cards for this date */}
                  <div className="space-y-2 ml-1 border-l-2 border-[#DBEAFE] dark:border-border/40 pl-3">
                    {dateCalls.map(call => (
                      <TimelineCallCard key={call.callLogId} call={call} />
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      ) : (
        <CallLogsEmptyState canCreate={canCreateCallLog} onAdd={handleAddClick} />
      )}

      {/* Add Call Log Drawer */}
      {programId && patientId && (
        <AddCallLogDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          programId={programId}
          patientId={patientId}
          patientName={patientName}
          patientCode={patientCode}
        />
      )}
    </div>
  );
});

